/**
 * ログイン後の共通レイアウト。
 *
 * サイドナビの並びは「メールを使うためにやること」順（ドメイン → アドレス →
 * 転送 → 迷惑メール → 接続情報）にしてある。機能の分類ではなく、
 * 契約者の作業順に並べる方が迷わない。
 */
import * as React from 'react';
import { Outlet } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppShell,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  UsageBar,
  formatBytes,
  type NavGroup,
  type NavItem,
} from '@mail-sv/ui';
import { ORG_PERMISSION } from '@mail-sv/shared';
import {
  Activity,
  AtSign,
  Bell,
  Bot,
  Building2,
  Check,
  ClipboardCheck,
  CreditCard,
  EyeOff,
  FileDown,
  FileText,
  FileSignature,
  Forward,
  Gauge,
  Globe,
  HardDrive,
  HelpCircle,
  Home,
  Image as ImageIcon,
  Inbox,
  KeyRound,
  LifeBuoy,
  ListChecks,
  LogOut,
  Mail,
  MailWarning,
  Megaphone,
  Palette,
  Reply,
  Scale,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  UserCog,
  Users,
  VenetianMask,
  Webhook,
} from 'lucide-react';
import { AppLink, useAppNavigate, useCurrentPath } from '../lib/nav.js';
import { canManageOrg, useAuth } from '../stores/auth.js';
import { logout, switchOrg } from '../lib/auth.js';
import { api, errorMessage } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import type { ConsentsRecorded, ConsentsView } from '../lib/api-types.js';
import { useOrg } from '../lib/hooks.js';
import { env } from '../env.js';
import { DevEnvBadge } from './dev-login-button.js';
import { NotificationBell } from '../routes/notifications.js';

/**
 * 一斉配信のナビ。
 *
 * **権限を 1 つも持たない契約者には項目ごと出さない。** 押しても 403 になる
 * 入口を残すと「使えるはずの機能が壊れている」に見える。
 *
 * 本来はプラン機能（`bulkSending`）でも出し分けたいが、`PLAN_CATALOG` の
 * `planFeaturesSchema` にまだそのフラグが無い。フラグが入ったら AND を取ること。
 */
function bulkNavGroup(permissions: readonly string[]): NavGroup | null {
  const has = (p: string): boolean => permissions.includes(p);
  const items: NavItem[] = [];

  if (has(ORG_PERMISSION.campaignRead)) {
    items.push({ key: 'campaigns', label: 'キャンペーン', href: '/bulk/campaigns', icon: Megaphone });
  }
  if (has(ORG_PERMISSION.contentRead)) {
    items.push({ key: 'contents', label: 'コンテンツ', href: '/bulk/contents', icon: FileText });
    items.push({ key: 'media', label: '画像ライブラリ', href: '/bulk/media', icon: ImageIcon });
    // 承認待ちは「放置されると誰も気づかない待ち行列」なので、必ず入口を出す。
    items.push({ key: 'reviews', label: '承認待ち', href: '/bulk/reviews', icon: ClipboardCheck });
    items.push({ key: 'library', label: 'コンテンツの管理', href: '/bulk/library', icon: Tags });
  }
  if (has(ORG_PERMISSION.contactRead)) {
    items.push({ key: 'lists', label: '宛先リスト', href: '/bulk/lists', icon: ListChecks });
    /*
     * 申込フォームは宛先リストの入口なので、リストの直後に置く。
     * 権限も `contactRead`（フォームは宛先を増やす手段）で揃えてある。
     */
    items.push({
      key: 'signup-forms',
      label: '申込フォーム',
      href: '/bulk/signup-forms',
      icon: FileSignature,
    });
  }
  /*
   * **送信元ストリーム。** verified になるまで一斉配信は出せないのに、
   * 状態を確認する画面が 1 枚も無かった。キャンペーンの前提なので
   * キャンペーンと同じ権限（`campaignRead`）で出す。
   */
  if (has(ORG_PERMISSION.campaignRead)) {
    items.push({ key: 'streams', label: '送信元ストリーム', href: '/bulk/streams', icon: Send });
  }
  if (has(ORG_PERMISSION.webhookRead)) {
    items.push({ key: 'webhooks', label: 'Webhook', href: '/bulk/webhooks', icon: Webhook });
  }
  if (has(ORG_PERMISSION.contentRead)) {
    items.push({ key: 'bulk-settings', label: '配信の設定', href: '/bulk/settings', icon: SlidersHorizontal });
  }

  if (items.length === 0) return null;
  return { key: 'bulk', label: '一斉配信', items };
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'home',
    items: [{ key: 'dashboard', label: 'ホーム', href: '/', icon: Home }],
  },
  {
    key: 'mail',
    label: 'メールの設定',
    items: [
      /*
       * **Webメールの入口をサイドナビにも置く。** 以前はダッシュボードの
       * カードからしか辿れず、他の画面へ移動した後は URL を打つ以外に
       * 戻る手段が無かった（メールを読むのが目的の契約者にはいちばん
       * 使う画面）。
       */
      { key: 'webmail', label: 'Webメール', href: '/webmail', icon: Inbox },
      { key: 'domains', label: 'ドメイン', href: '/domains', icon: Globe },
      { key: 'mailboxes', label: 'メールアドレス', href: '/mailboxes', icon: AtSign },
      { key: 'aliases', label: '転送・エイリアス', href: '/aliases', icon: Forward },
      /*
       * **プライベートアドレスをエイリアスの直下に置く。** どちらも
       * 「受け取るためのアドレスを増やす」ものなので隣に無いと探せない。
       * プランで使えない契約者にも項目は出す（押すと理由と変更導線が出る）。
       * ナビから消すと「機能が無い」のか「見つけられていない」のかが
       * 本人に区別できず、問い合わせになる。
       */
      {
        key: 'private-addresses',
        label: 'プライベートアドレス',
        href: '/private-addresses',
        icon: VenetianMask,
      },
      { key: 'autoresponder', label: '自動応答', href: '/autoresponder', icon: Reply },
      { key: 'spam', label: '迷惑メール対策', href: '/spam', icon: MailWarning },
      /*
       * **受信者のプライバシー保護と AI を迷惑メール対策の隣に置く。**
       * どちらも「届いたメールをどう扱うか」の設定で、作業順としてはここ。
       * プランで使えない契約者にも項目は出す（押すと理由とアップグレード
       * 導線が出る）。ナビから消すと「機能が無い」のか「見つけられて
       * いない」のかが本人に区別できず、必ず問い合わせになる。
       */
      { key: 'privacy', label: '外部画像の保護', href: '/privacy', icon: EyeOff },
      { key: 'ai', label: 'AI 機能', href: '/ai', icon: Bot },
      { key: 'mail-clients', label: 'メールソフト設定', href: '/mail-clients', icon: Mail },
      // Webメールの見た目と、お客様のドメインでの公開（カスタムホスト名）。
      { key: 'branding', label: 'Webメールの見た目', href: '/branding', icon: Palette },
    ],
  },
  {
    key: 'status',
    label: '利用状況',
    items: [
      { key: 'usage', label: '容量・送信数', href: '/usage', icon: HardDrive },
      { key: 'logs', label: 'メールの履歴', href: '/logs', icon: ScrollText },
      /*
       * **非同期処理の一覧。** ドメイン追加などは 202 で受理され、
       * 進捗を出していた画面を離れると行方を追えなくなっていた。
       */
      { key: 'jobs', label: '処理の状況', href: '/jobs', icon: Activity },
      { key: 'security', label: 'セキュリティ', href: '/security', icon: ShieldCheck },
    ],
  },
  {
    key: 'support',
    label: 'お困りのとき',
    items: [
      /*
       * **運営からのご連絡と、運営へのご連絡。** どちらも API は揃っていたのに
       * 画面が無く、`channels=['inapp']` の通知は誰にも読まれていなかった。
       */
      /*
       * **使い方ガイドを先頭に置く。** これまで製品の中にヘルプが 1 枚も無く、
       * ヘッダの「ヘルプ」は `VITE_SUPPORT_URL` が設定されているときだけ
       * 出る外部リンクだった（未設定の構成ではどこからも辿り着けない）。
       * 問い合わせる前に自分で解ける導線を、問い合わせ先の手前に置く。
       */
      { key: 'help', label: '使い方ガイド', href: '/help', icon: HelpCircle },
      { key: 'notifications', label: 'お知らせ', href: '/notifications', icon: Bell },
      { key: 'support', label: 'サポート', href: '/support', icon: LifeBuoy },
    ],
  },
  {
    key: 'contract',
    label: 'ご契約',
    items: [
      { key: 'billing', label: '料金プラン・請求', href: '/billing', icon: CreditCard },
      { key: 'members', label: 'メンバー', href: '/members', icon: Users },
      // 解約前のデータ持ち出しはご契約に属する作業なので、請求の隣に置く。
      { key: 'exports', label: 'データの書き出し', href: '/exports', icon: FileDown },
      { key: 'api-tokens', label: 'API トークン', href: '/api-tokens', icon: KeyRound },
      { key: 'settings', label: 'アカウント設定', href: '/settings', icon: Settings },
    ],
  },
];

export function AppLayout() {
  const currentPath = useCurrentPath();
  const { user } = useAuth();
  const org = useOrg();
  const navigate = useAppNavigate();
  const queryClient = useQueryClient();

  // 「メールの設定」の直後、「利用状況」の前に差し込む（作業順に並べる既存の方針）。
  const navGroups = React.useMemo(() => {
    const bulk = bulkNavGroup(user?.permissions ?? []);
    if (!bulk) return NAV_GROUPS;
    const at = NAV_GROUPS.findIndex((g) => g.key === 'status');
    const index = at < 0 ? NAV_GROUPS.length : at;
    return [...NAV_GROUPS.slice(0, index), bulk, ...NAV_GROUPS.slice(index)];
  }, [user?.permissions]);

  /**
   * 組織の切り替え。
   *
   * **切り替えるとサーバは今のセッションを失効させ、新しい組織スコープで
   * トークンを再発行する。** 前の組織のデータがキャッシュに残っていると
   * 切り替え直後に他組織の一覧が見えるので、`portal` 名前空間を丸ごと捨てる。
   */
  const switchOrgMut = useMutation({
    mutationFn: (organizationId: string) => switchOrg(organizationId),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['portal'] });
      navigate({ to: '/', replace: true });
    },
  });

  const logoutMut = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      // 別の契約者でログインし直したときに前の組織のデータが見えないよう、
      // portal 名前空間のキャッシュを丸ごと捨てる。
      queryClient.removeQueries({ queryKey: ['portal'] });
      navigate({ to: '/login', replace: true });
    },
  });

  const plan = org.data?.plan;
  const usage = org.data?.usage;

  return (
    <AppShell
      brand={{ name: env.brandName, sub: org.data?.displayName ?? 'コントロールパネル' }}
      navGroups={navGroups}
      currentPath={currentPath}
      storageKey="mail-sv.portal.sidebar.collapsed"
      renderLink={(item, content, className) => (
        <AppLink to={item.href} className={className}>
          {content}
        </AppLink>
      )}
      topBarRight={
        <div className="flex items-center gap-2">
          <DevEnvBadge />
          {/*
           * **未読のお知らせをヘッダに出す。** 容量超過の予告や料金改定の
           * 予告はここに来るが、一覧を開かない限り誰も気づかなかった。
           * 件数だけを軽い口（`/notifications/unread-count`）で引く。
           */}
          <NotificationBell onOpen={() => navigate({ to: '/notifications' })} />
          {/*
           * **ヘルプは常に出す。** 以前は `VITE_SUPPORT_URL` が設定されている
           * ときだけ外部サイトへ出る導線で、未設定の構成では**ヘッダにも
           * ナビにもヘルプが存在しなかった**。行き先は製品内の使い方ガイドにし、
           * 外部の窓口はその画面からたどれるようにしてある。
           */}
          <Button asChild variant="ghost" size="sm">
            <AppLink to="/help">
              <LifeBuoy aria-hidden />
              <span className="hidden sm:inline">ヘルプ</span>
            </AppLink>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-hover"
                aria-label="アカウントメニュー"
              >
                <UserCog className="size-4 text-muted-foreground" aria-hidden />
                <span className="hidden max-w-[12rem] truncate sm:inline">
                  {user?.displayName || user?.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>{org.data?.displayName ?? 'ご契約'}</DropdownMenuLabel>
              <div className="px-2 pb-2 text-xs text-muted-foreground">
                <p className="truncate">{user?.email}</p>
                <p className="mt-0.5">{ROLE_LABEL[user?.role ?? 'org_member']}</p>
              </div>
              {/*
               * **組織の切り替え。** 複数組織に所属する担当者は、API
               * （`POST /auth/portal/switch-org`）があるのに切り替える手段が
               * 画面に無く、いったんログアウトしても最後に発行された組織の
               * トークンに戻るだけで、事実上もう一方の組織へ入れなかった。
               * 1 件しか所属が無いときは出さない（意味の無い選択肢を作らない）。
               */}
              {(user?.organizations.length ?? 0) > 1 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>組織を切り替える</DropdownMenuLabel>
                  {user?.organizations.map((o) => (
                    <DropdownMenuItem
                      key={o.organizationId}
                      disabled={o.organizationId === user.organizationId || switchOrgMut.isPending}
                      onSelect={() => {
                        if (o.organizationId !== user.organizationId) {
                          switchOrgMut.mutate(o.organizationId);
                        }
                      }}
                    >
                      <Building2 aria-hidden />
                      <span className="truncate">{o.displayName}</span>
                      {o.organizationId === user.organizationId ? (
                        <Check className="ml-auto size-4" aria-hidden />
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate({ to: '/settings' })}>
                <Settings aria-hidden />
                アカウント設定
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate({ to: '/security' })}>
                <ShieldCheck aria-hidden />
                セキュリティ
              </DropdownMenuItem>
              {/*
               * **法定ページへの到達経路をログイン後にも必ず用意する。**
               * `/legal` は認証不要のページだが、ログイン後の画面から
               * 1 つもリンクが無いと、契約者は規約・特商法表記・SLA を
               * 読み直す手段を（URL を覚えている場合を除いて）失う。
               */}
              <DropdownMenuItem onSelect={() => navigate({ to: '/legal' })}>
                <Scale aria-hidden />
                規約・法令表示
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => logoutMut.mutate()}>
                <LogOut aria-hidden />
                ログアウト
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
      footer={
        plan && usage ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">プラン</span>
              <Badge variant="secondary" size="sm">
                {plan.name}
              </Badge>
            </div>
            <UsageBar
              used={usage.storageBytes}
              total={plan.limits.storageBytesTotal}
              label="使用容量"
              formatValue={(n) => formatBytes(n)}
            />
          </div>
        ) : null
      }
    >
      <GlobalBanners />
      <ConsentBanner />
      <Outlet />
    </AppShell>
  );
}

/**
 * 規約改定の再同意（`GET/POST /portal/consents`）。
 *
 * **`LEGAL_DOCUMENT_VERSIONS` を上げても、既存契約者が新版に同意する経路が
 * どこにも無かった。** 版だけ上がって同意の記録が古いままだと、改定後の
 * 条項を根拠にした停止も免責も主張できない。API は先にあったのに、
 * 呼ぶ画面が 1 枚も無かった（＝到達不能）ので、ここで繋ぐ。
 *
 * 出す場所をレイアウトにしたのは、**どの画面から入っても必ず目に入る**必要が
 * あるため。逆に、**書き込みは 1 つもブロックしない**（モーダルで塞がない）。
 * 「新しい規約に同意できないので解約したい」は正当な選択肢で、
 * 解約とエクスポートまで塞ぐと契約者を閉じ込めることになる。
 */
function ConsentBanner() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canAccept = canManageOrg(user);

  const consents = useQuery({
    queryKey: qk.consents(),
    queryFn: () => api.get<ConsentsView>('/consents'),
    // 版が上がるのは年に数回。画面遷移のたびに引かない。
    staleTime: 10 * 60_000,
    // 代理ログイン中に運営スタッフが契約者の代わりに同意してしまわないよう、
    // 取得自体は行うが下でボタンを出さない。
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.post<ConsentsRecorded>('/consents', { accepted: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.consents() }),
  });

  const pending = consents.data?.pending ?? [];
  if (pending.length === 0) return null;

  return (
    <div className="mb-5">
      <Alert variant="warning">
        <AlertTitle>規約が改定されました</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            {pending.map((d) => `${d.title}（第 ${d.version} 版）`).join('、')}
            が改定されました。内容をご確認のうえ、同意をお願いします。
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {pending.map((d) => (
              <Button asChild key={d.kind} variant="outline" size="sm">
                <AppLink to={`/legal/${d.kind}`}>{d.title}を読む</AppLink>
              </Button>
            ))}
            {canAccept && !user?.impersonated ? (
              <Button size="sm" onClick={() => accept.mutate()} disabled={accept.isPending}>
                {accept.isPending ? '記録しています…' : '同意する'}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {user?.impersonated
                  ? '代理操作中は同意を記録できません。'
                  : '同意の記録は管理者（オーナー）が行えます。'}
              </span>
            )}
          </div>
          {accept.isError ? (
            <p className="text-xs text-destructive">{errorMessage(accept.error)}</p>
          ) : null}
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * 今日から見た残日数（切り上げ）。過去なら 0。
 *
 * **時刻ではなく日数で出す。** 「あと 13 時間」と出しても契約者が採れる行動は
 * 変わらず、深夜に「あと 0 日」と表示されるだけで不安を煽る。
 */
function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

const ROLE_LABEL: Record<string, string> = {
  org_owner: '管理者（オーナー）',
  org_admin: '管理者',
  org_member: '一般メンバー',
};

/**
 * 全ページ上部に出し続ける警告。
 * 支払い遅延と契約停止は、気づかないまま受信できなくなるのが最悪なので、
 * 画面を移動しても消えないようレイアウト側で描く。
 */
function GlobalBanners() {
  const { user } = useAuth();
  const org = useOrg();
  const data = org.data;

  const banners: React.ReactNode[] = [];

  if (user?.impersonated) {
    banners.push(
      <Alert variant="warning" key="impersonated">
        <AlertTitle>運営スタッフが代理で操作しています</AlertTitle>
        <AlertDescription>
          サポート対応のため、運営スタッフがこの画面を閲覧しています。操作内容は監査記録に残ります。
        </AlertDescription>
      </Alert>,
    );
  }

  if (data?.status === 'suspended') {
    banners.push(
      <Alert variant="destructive" key="suspended">
        <AlertTitle>ご契約が停止中です</AlertTitle>
        <AlertDescription>
          メールの受信は継続していますが、送信が停止しています。お支払い状況をご確認ください。
        </AlertDescription>
      </Alert>,
    );
  } else if (data?.subscription?.status === 'trialing' && data.subscription.trialEndsAt) {
    /*
     * **無料期間の残りは、これまで画面に一度も出ていなかった。**
     *
     * `trialEndsAt` は `GET /portal/org` も `GET /billing/subscription` も
     * 返していたのに、描画側が 1 か所も無かった（`grep -rn "trial" *.tsx`
     * → 0 件）。一方で worker の `handleTrialClose` は毎日 02:00 JST に走り、
     * 決済手段の無い契約を `past_due` にして、猶予を過ぎれば送信停止まで
     * 進める。つまり契約者が事態を知る最初の機会は「送信が止まったあと」
     * だった。**気付ける材料をいちばん目に入る場所に置く。**
     *
     * 残り 3 日以内で destructive、7 日以内で warning、それ以前は info。
     * 最初から赤くすると、契約直後の全契約者に警告を出すことになる。
     */
    const daysLeft = daysUntil(data.subscription.trialEndsAt);
    banners.push(
      <Alert
        variant={daysLeft <= 3 ? 'destructive' : daysLeft <= 7 ? 'warning' : 'info'}
        key="trialing"
      >
        <AlertTitle>
          {daysLeft <= 0
            ? '無料期間が終了しました'
            : `無料でお試しいただける期間はあと ${daysLeft} 日です`}
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            {`${new Date(data.subscription.trialEndsAt).toLocaleDateString('ja-JP')} までにお支払い方法をご登録ください。`}
            ご登録がない場合、期間終了後にメールの送信を停止します（受信は停止しません）。
          </p>
          <Button asChild variant="outline" size="sm">
            <AppLink to="/billing">お支払い方法を登録する</AppLink>
          </Button>
        </AlertDescription>
      </Alert>,
    );
  } else if (data?.subscription?.status === 'past_due') {
    // 督促の期限そのものは `/portal/org` が返さない（`subscriptions` には
    // 督促期限の列が無く、請求書側の `due_at` にしかない）。現期間の終わりを
    // 目安として出し、正確な期日は請求画面へ誘導する。
    banners.push(
      <Alert variant="destructive" key="past_due">
        <AlertTitle>お支払いが確認できていません</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            {`${new Date(data.subscription.currentPeriodEnd).toLocaleDateString('ja-JP')} までに入金が確認できない場合、メールの送信を停止します。`}
            受信は停止しません。
          </p>
          <Button asChild variant="outline" size="sm">
            <AppLink to="/billing">お支払い情報を確認する</AppLink>
          </Button>
        </AlertDescription>
      </Alert>,
    );
  }

  const storageRatio =
    data && data.plan.limits.storageBytesTotal > 0
      ? data.usage.storageBytes / data.plan.limits.storageBytesTotal
      : 0;

  if (storageRatio >= 0.9) {
    banners.push(
      <Alert variant={storageRatio >= 1 ? 'destructive' : 'warning'} key="storage">
        <AlertTitle>
          {storageRatio >= 1 ? '容量の上限に達しました' : '容量がまもなく上限に達します'}
        </AlertTitle>
        <AlertDescription className="space-y-2">
          <p>
            上限に達すると新しいメールを受信できなくなります。不要なメールを削除するか、プランを変更してください。
          </p>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <AppLink to="/usage">使用状況を見る</AppLink>
            </Button>
            <Button asChild variant="outline" size="sm">
              <AppLink to="/billing/plan">プランを変更する</AppLink>
            </Button>
          </div>
        </AlertDescription>
      </Alert>,
    );
  }

  if (banners.length === 0) return null;
  return <div className="mb-5 space-y-3">{banners}</div>;
}

/** ヘルスチェック用途の軽量表示（ルート未一致時）。 */
export function NotFoundPage() {
  return (
    <div className="mx-auto max-w-md space-y-4 py-16 text-center">
      <Gauge className="mx-auto size-10 text-muted-foreground" aria-hidden />
      <h1 className="text-xl font-semibold">お探しのページは見つかりませんでした</h1>
      <p className="text-sm text-muted-foreground">
        URL が変更されたか、削除された可能性があります。
      </p>
      <Button asChild>
        <AppLink to="/">ホームへ戻る</AppLink>
      </Button>
    </div>
  );
}
